// Pure ERG policy: who decides whether the trainer is driven, and whether a
// given FTMS write is allowed out right now. No Bluetooth, no React, no state.

export const STEP_MODE = Object.freeze({
  ERG: "erg",
  FREE: "free",
  MAX: "max",
});

// Accepts only a genuine number, or a non-blank string that parses to a
// finite number, as a candidate marker value. Everything else (null,
// undefined, booleans, arrays, objects, blank/whitespace strings) is
// rejected up front rather than handed to Number()'s permissive coercion —
// Number(false) === 0, Number([]) === 0 and Number([1000]) === 1000 would
// otherwise silently masquerade as the free/max markers.
function toFinitePower(rawPower) {
  if (typeof rawPower === "number") {
    return Number.isFinite(rawPower) ? rawPower : null;
  }
  if (typeof rawPower === "string") {
    const trimmed = rawPower.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// Plan markers as stored in Firestore: power === 0 means "free ride",
// power === 1000 means "best effort". Anything else is a real wattage target.
export function resolveStepMode(rawPower) {
  const n = toFinitePower(rawPower);
  if (n === null) return STEP_MODE.ERG;
  if (n === 0) return STEP_MODE.FREE;
  if (n === 1000) return STEP_MODE.MAX;
  return STEP_MODE.ERG;
}

// Steps produced by PlanSetup, TrainingForm, WorkoutParser and FileImporter
// carry no mode at all. Anything unrecognised means "drive it normally".
export function normalizeStepMode(mode) {
  return mode === STEP_MODE.FREE || mode === STEP_MODE.MAX ? mode : STEP_MODE.ERG;
}

// Which mode applies at a given index of a plan.
//
// A step index that lands outside the plan has no prescribed effort: an empty
// plan, or elapsed time past the last step. That is a free ride — release the
// trainer — not an ERG target. Before this existed the caller read
// `steps[indexStep]?.mode` and normalizeStepMode turned the resulting undefined
// into "erg", so a plan-less session held the rider at 50 W for the whole ride.
//
// Note the distinction from normalizeStepMode, which must keep its behaviour: a
// step that EXISTS with no mode field is still "erg", because that is how every
// plan built by PlanSetup, TrainingForm, WorkoutParser and FileImporter looks.
export function resolveActiveStepMode(steps, indexStep) {
  const step = Array.isArray(steps) ? steps[indexStep] : undefined;
  if (!step) return STEP_MODE.FREE;
  return normalizeStepMode(step.mode);
}

// The trainer is driven only when hardware, rider and plan all agree.
export function isErgActive({ canErgControl, ergEnabled, stepMode } = {}) {
  if (!canErgControl) return false;
  if (!ergEnabled) return false;
  return normalizeStepMode(stepMode) === STEP_MODE.ERG;
}

// Whether an FTMS control-point write may go out right now.
//
// `allowWhilePaused` is a deliberate, narrow exemption for release commands.
// The pause guard exists so we never push power targets at a stopped rider,
// but a release is the one command that MUST reach the trainer while paused —
// without it the trainer stays locked on the last interval target.
//
// `blocked` is NOT exempted: it means control was never granted or was lost,
// so the caller has to re-request control before anything can be written.
export function shouldAllowFtmsWrite({
  visible,
  paused,
  blocked,
  simulate,
  canErgControl,
  hasService,
  allowWhilePaused = false,
} = {}) {
  if (!visible) return false;
  if (paused && !allowWhilePaused) return false;
  if (blocked) return false;
  if (!simulate) {
    if (!canErgControl) return false;
    if (!hasService) return false;
  }
  return true;
}

// Whether ERG is actually driving the trainer right now, as the toggle should
// render it. A plan step forces it off without touching the rider's own
// ergEnabled choice, which is restored when the step ends.
//
// This deliberately returns a boolean and no label: the display next to the
// switch already names the mode ("free ride", "manual", "best effort"), so a
// second sentence here would only be a source of drift.
export function isErgEffectivelyOn({ ergEnabled, stepMode } = {}) {
  return Boolean(ergEnabled) && normalizeStepMode(stepMode) === STEP_MODE.ERG;
}
